import React, { forwardRef, useImperativeHandle, useState } from 'react';
import { Button, Modal, Space, InputNumber, Typography } from 'antd';
import { Scene } from '@monorepo/types';
import { LoadingState, useLoading } from '@monorepo/react-components';
import { useVideoContext } from '../../../../hooks/use-video';
import { useTranslation } from 'react-i18next';

const { Text, Paragraph } = Typography;

export interface UpdateSceneDurationModalActions {
  open: () => void;
}

interface Props {
  scene: Scene;
}

const UpdateSceneDurationModal = forwardRef<
  UpdateSceneDurationModalActions,
  Props
>(({ scene }, ref) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [duration, setDuration] = useState(scene.duration);
  const { loadingState, updateLoadingState } = useLoading();
  const { editSceneDuration } = useVideoContext();

  useImperativeHandle(ref, () => ({
    open: () => {
      setIsOpen(true);
      setDuration(scene.duration);
    },
  }));

  const handleUpdate = async () => {
    try {
      updateLoadingState(LoadingState.Loading);
      await editSceneDuration({
        sceneId: scene._id,
        updateDto: {
          duration,
        },
      });
      handleCancel();
    } catch (error) {
      console.error('Failed to update scene duration:', error);
    } finally {
      updateLoadingState(LoadingState.Loaded);
    }
  };

  const handleCancel = () => {
    setIsOpen(false);
  };

  return (
    <Modal
      title={t('videoEditor.scene.durationModal.title')}
      open={isOpen}
      onCancel={handleCancel}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          {t('videoEditor.scene.durationModal.cancel')}
        </Button>,
        <Button
          key="update"
          type="primary"
          loading={loadingState === LoadingState.Loading}
          onClick={handleUpdate}
        >
          {t('videoEditor.scene.durationModal.update')}
        </Button>,
      ]}
    >
      <Space direction="vertical" style={{ width: '100%' }}>
        <Paragraph type="secondary" style={{ margin: 0 }}>
          {t('videoEditor.scene.durationModal.description')}
        </Paragraph>
        <InputNumber
          min={0.1}
          max={600}
          step={0.1}
          value={duration}
          onChange={(value) => setDuration(value || 0.1)}
          style={{ width: '100%' }}
          addonAfter={t('videoEditor.scene.durationModal.seconds')}
        />
      </Space>
    </Modal>
  );
});

export default UpdateSceneDurationModal;
